import { Component, OnInit } from '@angular/core';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable, startWith, map, debounceTime, distinctUntilChanged, switchMap, of, tap } from 'rxjs';
import { ProdutoService } from '../../../../shared/services/apis/scanntech/produto.service';
import { Produto } from '../../../../shared/services/models/scanntech/produto.model';
import { RelatorioService } from '../../../../shared/services/apis/scanntech/relatorio.service';
import { RelatorioRequest } from '../../../../shared/services/requests/scanntech/relatorio.request';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../../../shared/components/header/header.component';

type listaTiposPromocaoType = {
  id: number;
  nome: string;
  descricao: string;
};

@Component({
  selector: 'app-relatorio-vendas-resulth-scanntech',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    CommonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    HeaderComponent,
  ],
  templateUrl: './relatorio-vendas-resulth-scanntech.component.html',
  styleUrl: './relatorio-vendas-resulth-scanntech.component.css',
})
export class RelatorioVendasResulthScanntechComponent implements OnInit {
  public moduloEnum: ModuloEnum;
  public funcionalidadeEnum: FuncionalidadeEnum;

  // Lista de tipos de promoção
  public listaTiposPromocao: listaTiposPromocaoType[] = [
    {
      id: 1,
      nome: 'Leva Paga',
      descricao: 'Produtos(s) sem custo dentro de pack (leva M paga N)',
    },
    {
      id: 2,
      nome: 'Desconto Variável',
      descricao: 'Pack com desconto porcentual',
    },
    {
      id: 3,
      nome: 'Preço Fixo',
      descricao: 'Produto/Pack de produtos com preço fixo',
    },
    {
      id: 4,
      nome: 'Adicional Presente',
      descricao: 'Produtos(s) de presente',
    },
    {
      id: 5,
      nome: 'Adicional Desconto',
      descricao: 'Produto com desconto dentro de um pack',
    },
    {
      id: 6,
      nome: 'Desconto Fixo',
      descricao: 'Pack com desconto fixo',
    },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
    private produtoService: ProdutoService,
    private relatorioService: RelatorioService
  ) {
    this.moduloEnum = ModuloEnum.Integracao;
    this.funcionalidadeEnum = FuncionalidadeEnum.Integracao_ResulthScanntech;
  }

  public model: FormGroup;
  public _isLoading: boolean = false;
  public _isLoadingProduto: boolean = false;
  public produtosFiltrados: Observable<Produto[]>;

  ngOnInit(): void {
    this.model = this.formBuilder.group(
      {
        dataInicio: ['', Validators.required],
        dataFim: ['', Validators.required],
        nfce: [''],
        serie: [''],
        promocao: [''],
        produto: [''],
      },
      { validators: this.validarPeriodo }
    );

    // Observar mudanças nas datas para revalidar
    this.model.get('dataInicio')?.valueChanges.subscribe(() => {
      this.model.get('dataFim')?.updateValueAndValidity();
    });

    // Configurar o autocomplete para produtos
    this.produtosFiltrados = this.model.get('produto')!.valueChanges.pipe(
      startWith(''),
      tap(value => {
        // Define loading como true quando o usuário digita algo (antes do debounce)
        if (typeof value === 'string' && value.length >= 2) {
          this._isLoadingProduto = true;
        } else {
          this._isLoadingProduto = false;
        }
      }),
      debounceTime(2000), // Aguarda 2 segundos após o usuário parar de digitar
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value === 'string' && value.length >= 2) {
          return this.produtoService.getProdutos(value).pipe(
            map(response => {
              this._isLoadingProduto = false; // Define loading como false após receber a resposta
              return response.listaProduto || [];
            })
          );
        }
        this._isLoadingProduto = false; // Define loading como false quando não atende critérios
        return of([]);
      })
    );
  }

  // Validador personalizado para verificar se data início não é maior que data fim
  validarPeriodo = (formGroup: FormGroup): { [key: string]: any } | null => {
    const dataInicio = formGroup.get('dataInicio')?.value;
    const dataFim = formGroup.get('dataFim')?.value;

    if (dataInicio && dataFim) {
      const inicio = new Date(dataInicio);
      const fim = new Date(dataFim);

      if (inicio > fim) {
        formGroup.get('dataFim')?.setErrors({ dataInvalida: true });
        return { periodoInvalido: true };
      } else {
        // Remove o erro se as datas estão corretas
        const errors = formGroup.get('dataFim')?.errors;
        if (errors?.['dataInvalida']) {
          delete errors['dataInvalida'];
          const hasOtherErrors = Object.keys(errors).length > 0;
          formGroup.get('dataFim')?.setErrors(hasOtherErrors ? errors : null);
        }
      }
    }
    return null;
  };

  exportar(): void {
    if (this.model.invalid || this._isLoading) {
      if (this.model.errors?.['periodoInvalido']) {
        this.alertService.show('Erro', 'A data inicial não pode ser maior que a data final', AlertType.error);
      } else {
        this.alertService.show('Erro', 'Preencha todos os campos obrigatórios', AlertType.error);
      }
      return;
    }

    this._isLoading = true;

    // Preparar os dados do formulário
    const formData = this.model.value;

    const relatorioRequest: RelatorioRequest = {
      dataInicial: this.formatarData(formData.dataInicio),
      dataFinal: this.formatarData(formData.dataFim),
      numeroNota: formData.nfce || undefined,
      serie: formData.serie || undefined,
      codigoBarras: formData.produto?.codigoBarras || undefined,
      idTipoPromocao: formData.promocao?.id || undefined,
    };

    console.log('Enviando dados para gerar relatório:', relatorioRequest);

    this.relatorioService.gerarRelatorio(relatorioRequest).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro inesperado', response.errorDescription, AlertType.error);
        } else {
          this.alertService.show(
            'Sucesso',
            'Relatório foi enviado para processamento e será gerado em breve',
            AlertType.success
          );
          this.router.navigate(['/integracao/resulth-scanntech']);
        }
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  private formatarData(data: any): string {
    if (!data) return '';

    // Se já é uma string no formato correto, retorna
    if (typeof data === 'string') return data;

    // Se é um objeto Date, formata para ISO string
    if (data instanceof Date) {
      return data.toISOString().split('T')[0];
    }

    return '';
  }

  get buttonDesativado(): boolean {
    return this.model.invalid || this._isLoading;
  }

  displayProduto(produto: Produto): string {
    return produto ? `${produto.codigoBarras} - ${produto.nomeProduto}` : '';
  }

  displayPromocao(promocao: listaTiposPromocaoType): string {
    return promocao ? `${promocao.nome} - ${promocao.descricao}` : '';
  }
}
